"use client";

import dynamic from "next/dynamic";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import type {
  PolymarketModalData,
  KalshiModalData,
  PredictModalData,
  HyperliquidModalData,
} from "@/stores/useTradingModalStore";
import { useAuth } from "@/hooks/useAuth";

const TradingModal = dynamic(() => import("../TradingModal"), { ssr: false });
const KalshiAdapterWrapper = dynamic(() => import("@/lib/trading/KalshiAdapterWrapper"), { ssr: false });
const PredictAdapterWrapper = dynamic(() => import("@/lib/trading/PredictAdapterWrapper"), { ssr: false });
const HyperliquidAdapterWrapper = dynamic(() => import("@/lib/trading/HyperliquidAdapterWrapper"), { ssr: false });

/**
 * Single global dispatcher for the trading modal across all sources.
 * Reads the open state + payload from `useTradingModalStore` and
 * routes to the right TradingModal / adapter based on `data.source`.
 *
 * Mounted once at the app layout level (see AppLayoutWithSidebar.tsx)
 * so any surface — markets grid, sidebar widget, activity feed,
 * portfolio — can call `useTradingModalStore.openX(...)` and the
 * modal lights up here.
 */
export function SharedTradingModals() {
  const { isOpen, data, close } = useTradingModalStore();
  const { currentWalletAddress } = useAuth();
  if (!isOpen || !data) return null;

  if (data.source === "polymarket") {
    const d = data as PolymarketModalData;
    return (
      <TradingModal
        isOpen
        onClose={close}
        marketSlug={d.marketSlug}
        outcome={d.outcome}
        outcomePrice={d.outcomePrice}
        marketQuestion={d.marketQuestion}
        marketImage={d.marketImage}
        actualOutcome={d.actualOutcome}
        outcomes={d.outcomes}
        outcomeColor={d.outcomeColor}
        altOutcomeColor={d.altOutcomeColor}
        altOutcomePrice={d.altOutcomePrice}
        altActualOutcome={d.altActualOutcome}
        referralPostId={d.referralPostId ?? null}
        referralAuthorId={d.referralAuthorId ?? null}
        traderInfo={d.traderInfo}
        onCopyTrader={d.onCopyTrader}
        quickBuyAmounts={d.quickBuyAmounts}
        clobTokenIds={d.clobTokenIds}
        conditionId={d.conditionId}
        mode={d.mode}
        positionShares={d.positionShares}
        positionAvgPrice={d.positionAvgPrice}
        onPriceAlert={d.onPriceAlert}
        disableSwipeDismiss={d.disableSwipeDismiss}
      />
    );
  }

  if (data.source === "predict") {
    return (
      <PredictAdapterWrapper
        data={data as PredictModalData}
        onClose={close}
        displayWalletAddress={currentWalletAddress || undefined}
      />
    );
  }

  if (data.source === "hyperliquid") {
    return (
      <HyperliquidAdapterWrapper
        data={data as HyperliquidModalData}
        onClose={close}
        displayWalletAddress={currentWalletAddress || undefined}
      />
    );
  }

  const d = data as KalshiModalData;
  return (
    <KalshiAdapterWrapper
      isOpen
      onClose={close}
      marketTicker={d.marketTicker}
      dflowTicker={d.dflowTicker}
      marketTitle={d.marketTitle}
      outcome={d.outcome}
      currentPrice={d.currentPrice}
      altPrice={d.altPrice}
      yesMint={d.yesMint}
      noMint={d.noMint}
      imageUrl={d.imageUrl}
      mode={d.mode}
      positionSize={d.positionSize}
      avgEntryPrice={d.avgEntryPrice}
      referralPostId={d.referralPostId}
      referralAuthorId={d.referralAuthorId}
    />
  );
}
